import { DownloadCOC, GetCOCExportCount } from '@/apis/shipment';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { saveBlobAsFile } from '@/pages/Shipments/components/fileDownload';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { AxiosResponse } from 'axios';
import { message } from 'antd';
import { useCallback } from 'react';
import type { ShipmentRow } from '../types';

/**
 * Converts AxiosResponse<Blob> to compatible format for saveBlobAsFile
 */
function prepareBlobForDownload(res: AxiosResponse<Blob>) {
  const headers: Record<string, string> = {};
  Object.entries(res.headers).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      headers[key] = value.join('; ');
    } else if (typeof value === 'number') {
      headers[key] = value.toString();
    } else if (value !== undefined) {
      headers[key] = value;
    }
  });

  return {
    data: res.data,
    headers,
  };
}

export const useExportCOC = () => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();

  const fetchCOCCount = useCallback(
    async (shipment: ShipmentRow) => {
      const selectedIds = [shipment.id];
      const model = {
        shipment_ids: selectedIds,
      };
      return GetCOCExportCount(token, model);
    },
    [token]
  );

  const handleClickExportCOC = useCallback(
    async ({
      shipment,
      exportOptions,
    }: {
      shipment: ShipmentRow;
      exportOptions: Record<
        'statusUpdates' | 'comments' | 'alerts' | 'pods',
        boolean
      >;
    }) => {
      try {
        const selectedIds = [shipment.id];
        const model = {
          shipment_ids: selectedIds,
          include_status_updates: exportOptions?.statusUpdates || false,
          include_comments: exportOptions?.comments || false,
          include_alerts: exportOptions?.alerts || false,
          include_pods: exportOptions?.pods || false,
        };

        const res = await DownloadCOC(token, model); // full response
        const blobRes = prepareBlobForDownload(res);
        const zipName =
          theme.themeType === 2 ? 'Transit_history' : 'Chain_of_custody';
        saveBlobAsFile(
          blobRes,
          `${zipName}_export_${new Date().toISOString().slice(0, 10)}.zip`
        );
      } catch (err) {
        console.error(err);
        message.error(
          `We couldn't prepare your ${themeT('chainOfCustody', theme.themeType)?.toLowerCase()} export. Please try again or contact support.`
        );
        unauthorize(err);
        throw err;
      }
    },
    [token, unauthorize]
  );

  return {
    fetchCOCCount,
    handleClickExportCOC,
  };
};
